import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import PhotoGallery from '../components/PhotoGallery';
import { SpeciesBioProfile } from '../components/bioprofile/BioProfile';
import QRCodeLabel from '../components/QRCodeLabel';
import CreatedCodes from '../components/CreatedCodes';
import FieldError, { errorIdFor, invalidBorder, invalidProps } from '../components/FieldError';
import {
  checkBand, collectErrors, hasErrors, numberInRange, required,
  serverErrorMessage, useFieldErrors, validationMessage,
} from '../utils/formValidation';
import { PLATE_STATUS_COLORS, SLANT_STATUS_COLORS } from '../constants/statusColors';
import useFocusOnOpen, { focusElement } from '../utils/useFocusOnOpen';
import OrganicEmptyState from '../components/organic/OrganicEmptyState.jsx';
import OrganicLoader from '../components/organic/OrganicLoader.jsx';

/*
 * Strains — the culture bank's named lines.
 *
 * A strain is the thing that persists across every plate, slant and DNA isolate
 * derived from one original line: its accession code, its cultivation envelope,
 * and its lineage. /collections is a different idea entirely (where a specimen
 * was picked); this is the curated library.
 *
 * Two notes for whoever edits this next:
 *
 *   1. The medium dropdown loads /protocols directly rather than reusing
 *      ReferenceSelect. ReferenceSelect is hardwired to /dna/{category} AND to
 *      a response shaped {id, name}; /protocols answers {protocol_id, name}.
 *      Generalising it would need two new props and a change to a component
 *      four DNA fields already depend on, to save a dozen lines here.
 *
 *   2. `holdings` on the detail modal is SPECIES-scoped, not strain-scoped —
 *      nothing in the schema links a plate or slant to a strain, so the API
 *      resolves through the species. The panel says so in its own subtitle.
 *      Do not relabel it without adding the strain_id columns first.
 */

const GROWTH_STAGES = ['culture', 'spawn', 'fruiting'];
const SORT_FIELDS = ['created_at', 'isolate_code', 'species', 'acquisition_date'];
const PAGE_SIZE = 25;

const emptyRequirement = (stage) => ({
  growth_stage: stage,
  temp_min: '', temp_opt: '', temp_max: '',
  ph_min: '', ph_opt: '', ph_max: '',
  humidity_min: '', humidity_max: '',
  substrate_type: '', protocol_id: '', incubation_days: '',
  light_regime: '', notes: '',
});

const emptyForm = () => ({
  isolate_code: '',
  species: '',
  source: '',
  acquisition_date: '',
  notes: '',
  requirements: Object.fromEntries(GROWTH_STAGES.map(s => [s, emptyRequirement(s)])),
  lineage: [],
});

/* A requirement row is "empty" when the operator opened its card and typed
 * nothing. Sending it anyway would create a row of all-NULLs that reads on the
 * detail panel as "we measured this and found nothing", which is a different
 * claim from "nobody has measured it". */
const requirementIsBlank = (r) =>
  Object.entries(r).every(([k, v]) =>
    k === 'growth_stage' || v === '' || v === null || v === undefined);

const num = (v) => (v === '' || v === null || v === undefined ? null : Number(v));

/* What the server (routes/strains.py::_requirement_problem) and migration
 * 0001's CHECK constraints accept, checked here first so a bad value is named
 * beside its own input instead of coming back as a refusal. Listed in the
 * order the inputs appear: that is the order focus moves through them. */
const REQUIREMENT_LIMITS = {
  temp_min: {}, temp_opt: {}, temp_max: {},
  ph_min: { min: 0, max: 14 }, ph_opt: { min: 0, max: 14 }, ph_max: { min: 0, max: 14 },
  humidity_min: { min: 0, max: 100 }, humidity_max: { min: 0, max: 100 },
  // sgr_incubation_days_positive: a whole number of days, above zero.
  incubation_days: { min: 0, minExclusive: true, integer: true },
};
const BANDS = [
  { min: 'temp_min', opt: 'temp_opt', max: 'temp_max' },
  { min: 'ph_min', opt: 'ph_opt', max: 'ph_max' },
  { min: 'humidity_min', max: 'humidity_max' },
];

const reqField = (stage, field) => `${stage}.${field}`;
const passageField = (i) => `lineage.${i}.passage_number`;
const inputId = (field) => `strain-${field.replace(/\./g, '-')}`;

/* Every problem with the form, keyed by field, in screen order. A species is
 * required (a strain is a line OF something -- an empty form used to save as
 * a strain of nothing) -- except while editing a line that was banked
 * unidentified (a primary isolate, a plate isolation): that one stays
 * editable without anyone inventing a species. A stage left entirely blank is
 * not checked, because it is not sent. */
function validateStrainForm(form, { speciesRequired = true } = {}) {
  const pairs = [['species', speciesRequired ? required(form.species, 'form.speciesRequired') : null]];
  for (const stage of GROWTH_STAGES) {
    const r = form.requirements[stage];
    if (requirementIsBlank(r)) continue;
    const order = Object.assign({}, ...BANDS.map((band) => checkBand(r, band)));
    for (const [field, limits] of Object.entries(REQUIREMENT_LIMITS)) {
      pairs.push([reqField(stage, field), numberInRange(r[field], limits) || order[field] || null]);
    }
  }
  form.lineage.forEach((link, i) => {
    // strain_lineage_passage_positive: 0 or more, whole. Rows with no parent
    // are dropped before sending, so they are not checked either.
    if (link.parent_strain_id) {
      pairs.push([passageField(i), numberInRange(link.passage_number, { min: 0, integer: true })]);
    }
  });
  return collectErrors(pairs);
}

/* A refusal the server put on one field, as that field's error. Anything
 * else is said once, above the buttons (refusalMessage). */
const SERVER_REQUIREMENT_ERRORS = {
  not_a_number: { code: 'notANumber' },
  band_min_above_max: { code: 'minAboveMax' },
  optimum_below_min: { code: 'optBelowMin' },
  optimum_above_max: { code: 'optAboveMax' },
  ph_out_of_range: { code: 'outOfRange', min: 0, max: 14 },
  humidity_out_of_range: { code: 'outOfRange', min: 0, max: 100 },
  incubation_days_invalid: { code: 'aboveMin', min: 0 },
};

function serverFieldErrors(err) {
  const data = err?.response?.data || {};
  if (data.code === 'species_required') {
    return { species: { code: 'required', key: 'form.speciesRequired' } };
  }
  if (data.code === 'isolate_code_taken') {
    return { isolate_code: { code: 'taken', key: 'form.isolateCodeTaken', isolate_code: data.isolate_code || '' } };
  }
  if (data.stage && data.field && SERVER_REQUIREMENT_ERRORS[data.code]) {
    return { [reqField(data.stage, data.field)]: SERVER_REQUIREMENT_ERRORS[data.code] };
  }
  return null;
}

const SERVER_FORM_ERRORS = {
  parent_not_found: 'errors.parentNotFound',
  parent_invalid: 'errors.parentNotFound',
  lineage_cycle: 'errors.lineageCycle',
};

/* Render a band as "min – opt – max", collapsing whatever is missing, so a
 * half-known envelope still reads as a fact rather than as a gap. */
const formatBand = (min, opt, max, unit, dash) => {
  const parts = [];
  if (min !== null && min !== undefined) parts.push(min);
  if (opt !== null && opt !== undefined) parts.push(`(${opt})`);
  if (max !== null && max !== undefined) parts.push(max);
  if (parts.length === 0) return dash;
  return `${parts.join(' – ')}${unit || ''}`;
};

const badge = (text, colorMap, label) => {
  const style = colorMap[text] || { background: 'var(--c-bg-muted)', color: 'var(--c-text-body-fg)' };
  return (
    <span style={{
      ...style, padding: '2px 10px', borderRadius: 12, fontSize: 12,
      fontWeight: 600, whiteSpace: 'nowrap', marginInlineEnd: 6,
      display: 'inline-block', marginBottom: 4,
    }}>
      {label}
    </span>
  );
};

export default function Strains() {
  const { t } = useTranslation(['strains', 'common']);
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Mirrors ELEVATED_ROLES / DELETE_ROLES in routes/strains.py. Hiding a button
  // is courtesy; the server is the control.
  const canEdit = user?.roles?.some(r =>
    ['collection_manager', 'lab_technician', 'lab_manager'].includes(r));
  const canArchive = user?.roles?.some(r =>
    ['collection_manager', 'lab_manager'].includes(r));

  const [createdCodes, setCreatedCodes] = useState([]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tableExpanded, setTableExpanded] = useState(true);

  const [query, setQuery] = useState('');
  const [speciesFilter, setSpeciesFilter] = useState('');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [sort, setSort] = useState('created_at');
  const [order, setOrder] = useState('desc');
  const [offset, setOffset] = useState(0);

  const [protocols, setProtocols] = useState([]);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  // The species the strain being edited already had (null for an
  // unidentified line, or while creating). It decides whether the species
  // may be left empty; see validateStrainForm.
  const [editingSpecies, setEditingSpecies] = useState(null);
  const speciesRequired = !editingId || !!editingSpecies;
  // Focus goes to the form's heading when it opens (its opener may be an
  // empty state that disappears), and to the search box after the filters
  // are cleared (the Clear button goes with the empty state it sat in).
  const formHeadingRef = useFocusOnOpen(showForm);
  const searchRef = useRef(null);
  const [form, setForm] = useState(emptyForm());
  const [formError, setFormError] = useState('');
  // { [field]: error } shown beside each field (see validateStrainForm);
  // focus follows them to the first.
  const {
    errors: fieldErrors, show: showFieldErrors, clear: clearFieldError,
    reset: resetFieldErrors, containerRef: formRef,
  } = useFieldErrors();
  const [saving, setSaving] = useState(false);
  const fieldError = (field) => fieldErrors[field];

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [archiveTarget, setArchiveTarget] = useState(null);
  const [archiving, setArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    const params = { limit: PAGE_SIZE, offset, sort, order };
    if (query.trim()) params.q = query.trim();
    if (speciesFilter.trim()) params.species = speciesFilter.trim();
    if (includeArchived) params.include_archived = 'true';
    api.get('/strains', { params })
      .then(res => {
        setItems(res.data.items || []);
        setTotal(res.data.total || 0);
      })
      .catch(err => setError(serverErrorMessage(t, err, {}, 'errors.loadFailed')))
      .finally(() => setLoading(false));
  }, [offset, sort, order, query, speciesFilter, includeArchived, t]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get('/protocols')
      .then(res => setProtocols(res.data || []))
      .catch(() => setProtocols([]));
  }, []);

  // Any filter change puts you back on page one; staying on page 4 of a result
  // set that now has one page shows an empty table and looks like a failure.
  useEffect(() => { setOffset(0); }, [query, speciesFilter, includeArchived, sort, order]);

  const openDetail = (id) => {
    setDetailLoading(true);
    setDetail(null);
    api.get(`/strains/${id}`)
      .then(res => setDetail(res.data))
      .catch(err => setError(serverErrorMessage(t, err, {}, 'errors.loadFailed')))
      .finally(() => setDetailLoading(false));
  };

  // Arrived from a link to one strain (the home hub's bank table, a
  // resolveLinkedRecordNav target): open it, then clear the navigation state
  // so back/forward doesn't reopen it.
  useEffect(() => {
    if (location.state?.openStrainId) {
      openDetail(location.state.openStrainId);
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const openCreate = () => {
    setEditingId(null);
    setEditingSpecies(null);
    setForm(emptyForm());
    setFormError('');
    resetFieldErrors();
    setShowForm(true);
  };

  const openEdit = (strain) => {
    const reqs = Object.fromEntries(GROWTH_STAGES.map(s => [s, emptyRequirement(s)]));
    (strain.growth_requirements || []).forEach(r => {
      if (!reqs[r.growth_stage]) return;
      reqs[r.growth_stage] = {
        growth_stage: r.growth_stage,
        temp_min: r.temp_min ?? '', temp_opt: r.temp_opt ?? '', temp_max: r.temp_max ?? '',
        ph_min: r.ph_min ?? '', ph_opt: r.ph_opt ?? '', ph_max: r.ph_max ?? '',
        humidity_min: r.humidity_min ?? '', humidity_max: r.humidity_max ?? '',
        substrate_type: r.substrate_type ?? '',
        protocol_id: r.protocol_id ?? '',
        incubation_days: r.incubation_days ?? '',
        light_regime: r.light_regime ?? '',
        notes: r.notes ?? '',
      };
    });
    setEditingId(strain.id);
    setEditingSpecies((strain.species || '').trim() || null);
    setForm({
      isolate_code: strain.isolate_code || '',
      species: strain.species || '',
      source: strain.source || '',
      acquisition_date: strain.acquisition_date || '',
      notes: strain.notes || '',
      requirements: reqs,
      lineage: (strain.lineage?.parents || []).map(p => ({
        parent_strain_id: p.strain_id,
        passage_number: p.passage_number ?? '',
        generation_code: p.generation_code ?? '',
      })),
    });
    setFormError('');
    resetFieldErrors();
    setShowForm(true);
  };

  const setReqField = (stage, field, value) => {
    setForm(f => ({
      ...f,
      requirements: {
        ...f.requirements,
        [stage]: { ...f.requirements[stage], [field]: value },
      },
    }));
    // The whole stage: a band error sits on one end but either end fixes it.
    clearFieldError(name => name.startsWith(`${stage}.`));
  };

  const setLineageField = (i, field, value) => {
    setForm(f => {
      const next = [...f.lineage];
      next[i] = { ...next[i], [field]: value };
      return { ...f, lineage: next };
    });
    clearFieldError(passageField(i));
  };

  const submit = (e) => {
    e.preventDefault();
    setFormError('');

    // Nothing is sent while a field is wrong: each says why beside itself,
    // and focus goes to the first.
    const errors = validateStrainForm(form, { speciesRequired });
    if (hasErrors(errors)) {
      showFieldErrors(errors);
      return;
    }
    resetFieldErrors();

    const growth_requirements = GROWTH_STAGES
      .map(s => form.requirements[s])
      .filter(r => !requirementIsBlank(r))
      .map(r => ({
        growth_stage: r.growth_stage,
        temp_min: num(r.temp_min), temp_opt: num(r.temp_opt), temp_max: num(r.temp_max),
        ph_min: num(r.ph_min), ph_opt: num(r.ph_opt), ph_max: num(r.ph_max),
        humidity_min: num(r.humidity_min), humidity_max: num(r.humidity_max),
        substrate_type: r.substrate_type || null,
        protocol_id: r.protocol_id || null,
        incubation_days: num(r.incubation_days),
        light_regime: r.light_regime || null,
        notes: r.notes || '',
      }));

    const lineage = form.lineage
      .filter(l => l.parent_strain_id)
      .map(l => ({
        parent_strain_id: l.parent_strain_id,
        passage_number: num(l.passage_number),
        generation_code: l.generation_code || null,
      }));

    const species = form.species.trim();
    const payload = {
      source: form.source || null,
      acquisition_date: form.acquisition_date || null,
      notes: form.notes || '',
      growth_requirements,
      lineage,
    };
    // Left out when an unidentified line is edited and still has no species:
    // the server keeps what it has. A species it already had is always sent,
    // so clearing one is refused there too.
    if (species || speciesRequired) payload.species = species;
    // isolate_code is server-generated when left blank, and immutable after
    // creation — it is an accession number, and renaming one silently breaks
    // every label already printed and stuck to a tube.
    if (!editingId && form.isolate_code.trim()) {
      payload.isolate_code = form.isolate_code.trim();
    }

    setSaving(true);
    const request = editingId
      ? api.put(`/strains/${editingId}`, payload)
      : api.post('/strains', payload);

    request
      .then((res) => {
        // A new strain's accession code, as a printable label.
        if (!editingId && res?.data?.isolate_code) setCreatedCodes([res.data.isolate_code]);
        setShowForm(false);
        setEditingId(null);
        setForm(emptyForm());
        load();
      })
      .catch((err) => {
        // Said in the reader's language, by the refusal's code -- never the
        // server's English sentence.
        const onField = serverFieldErrors(err);
        if (onField) { showFieldErrors(onField); return; }
        setFormError(err?.response?.status === 403
          ? t('errors.notAllowed')
          : serverErrorMessage(t, err, SERVER_FORM_ERRORS, 'errors.saveFailed'));
      })
      .finally(() => setSaving(false));
  };

  const confirmArchive = () => {
    if (!archiveTarget) return;
    setArchiving(true);
    setArchiveError('');
    api.delete(`/strains/${archiveTarget.id}`)
      .then(() => {
        setArchiveTarget(null);
        setDetail(null);
        load();
      })
      .catch(err => setArchiveError(serverErrorMessage(t, err, {}, 'errors.archiveFailed')))
      .finally(() => setArchiving(false));
  };

  const restore = (strain) => {
    api.put(`/strains/${strain.id}/restore`, {})
      .then(() => { setDetail(null); load(); })
      .catch(err => setError(serverErrorMessage(t, err, {}, 'errors.restoreFailed')));
  };

  // Only a search or a species narrows the list (archived strains widen it),
  // so only they turn "nothing yet" into "nothing matches".
  const filtered = !!(query.trim() || speciesFilter.trim());
  const clearFilters = () => {
    setQuery('');
    setSpeciesFilter('');
    focusElement(searchRef.current);
  };

  const pageStart = total === 0 ? 0 : offset + 1;
  const pageEnd = Math.min(offset + PAGE_SIZE, total);

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ margin: '0 0 4px' }}>{t('title')}</h2>
          <p style={{ margin: 0, color: 'var(--c-text-muted-fg)', fontSize: 14, maxWidth: 620 }}>
            {t('subtitle')}
          </p>
          <CreatedCodes codes={createdCodes} onDismiss={() => setCreatedCodes([])} />
        </div>
        {canEdit && (
          <button type="button" onClick={openCreate} style={btnStyle('var(--c-primary)')} title={t('tip.new')}>
            {t('newBtn')}
          </button>
        )}
      </div>

      {/* ── filters ── */}
      <div style={{ ...formStyle, marginTop: 20, marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ ...labelStyle, flex: '2 1 240px' }}>
            {t('filters.search')}
            <input type="search" ref={searchRef} value={query} onChange={e => setQuery(e.target.value)}
                   placeholder={t('filters.searchPlaceholder')} style={inputStyle} title={t('tip.search')} />
          </label>
          <label style={{ ...labelStyle, flex: '1 1 180px' }}>
            {t('filters.species')}
            <input type="text" value={speciesFilter} onChange={e => setSpeciesFilter(e.target.value)}
                   placeholder={t('filters.speciesPlaceholder')} style={inputStyle} dir="ltr" title={t('tip.speciesFilter')} />
          </label>
          <label style={{ ...labelStyle, flex: '0 1 170px' }}>
            {t('filters.sort')}
            <select value={sort} onChange={e => setSort(e.target.value)} style={inputStyle} title={t('tip.sort')}>
              {SORT_FIELDS.map(f => (
                <option key={f} value={f}>{t(`filters.sortBy.${f}`)}</option>
              ))}
            </select>
          </label>
          <label style={{ ...labelStyle, flex: '0 1 140px' }}>
            {t('filters.order')}
            <select value={order} onChange={e => setOrder(e.target.value)} style={inputStyle} title={t('tip.order')}>
              <option value="desc">{t('filters.orderDesc')}</option>
              <option value="asc">{t('filters.orderAsc')}</option>
            </select>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, paddingBottom: 8 }}>
            <input type="checkbox" checked={includeArchived}
                   onChange={e => setIncludeArchived(e.target.checked)} title={t('tip.includeArchived')} />
            {t('filters.includeArchived')}
          </label>
        </div>
      </div>

      {loading && (
        <OrganicLoader block motif="lineage" label={t('common:loading')} slowLabel={t('common:stillWorking')} />
      )}
      {error && <p role="alert" style={{ color: 'var(--c-danger-fg)' }}>{error}</p>}

      {!loading && !error && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
            <button type="button" onClick={() => setTableExpanded(e => !e)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-primary-fg)', fontSize: 14, fontWeight: 600, padding: '4px 0' }} title={t('tip.toggleRecords')}>
              {tableExpanded
                ? t('table.hideRecords', { count: total })
                : t('table.showRecords', { count: total })}
            </button>
            {total > 0 && (
              <span style={{ fontSize: 13, color: 'var(--c-text-muted-fg)' }}>
                {t('pagination.showing', { from: pageStart, to: pageEnd, total })}
              </span>
            )}
          </div>

          {tableExpanded && (items.length === 0 ? (
            filtered ? (
              <OrganicEmptyState kind="strains" compact title={t('noMatches')} hint={t('empty.filteredHint')}
                action={(
                  <button type="button" onClick={clearFilters} style={btnStyle('var(--c-text-muted)')}
                          data-testid="strains-clear-filters" title={t('empty.clearFiltersTip')}>
                    {t('empty.clearFilters')}
                  </button>
                )} />
            ) : (
              <OrganicEmptyState kind="strains" title={t('noStrainsYet')} hint={t('empty.hint')}
                action={canEdit ? (
                  <button type="button" onClick={openCreate} style={btnStyle('var(--c-primary)')}
                          data-testid="strains-empty-create" title={t('tip.new')}>
                    {t('empty.action')}
                  </button>
                ) : null} />
            )
          ) : (
            <>
              <div className="table-wrap">
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
                  <thead>
                    <tr style={{ background: 'var(--c-bg-muted-2)' }}>
                      {['isolateCode', 'species', 'source', 'acquired', 'requirements', 'lineage', 'actions']
                        .map(h => <th key={h} style={thStyle}>{t(`table.${h}`)}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {items.map(s => (
                      <tr key={s.id}
                          style={{ borderBottom: '1px solid var(--c-bg-muted-3)', cursor: 'pointer', opacity: s.is_archived ? 0.55 : 1 }}
                          onClick={() => openDetail(s.id)} title={t('tip.openStrain')}>
                        <td style={{ ...tdStyle, fontFamily: 'monospace' }} dir="ltr">
                          {s.isolate_code}
                          {s.is_archived && (
                            <span style={{ marginInlineStart: 8, fontSize: 11, color: 'var(--c-text-muted-fg)', fontFamily: 'inherit' }}>
                              {t('table.archived')}
                            </span>
                          )}
                        </td>
                        <td style={tdStyle} dir="ltr">
                          <em>{s.species || '—'}</em>
                        </td>
                        <td style={tdStyle}>{s.source || '—'}</td>
                        <td style={tdStyle}>
                          {s.acquisition_date
                            ? new Date(s.acquisition_date).toLocaleDateString()
                            : '—'}
                        </td>
                        <td style={tdStyle}>{s.requirement_count || 0}</td>
                        <td style={tdStyle}>
                          {t('table.lineageCounts', {
                            parents: s.parent_count || 0,
                            children: s.child_count || 0,
                          })}
                        </td>
                        <td style={tdStyle} onClick={e => e.stopPropagation()}>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            <button type="button" onClick={() => openDetail(s.id)}
                                    style={smallBtn('var(--c-primary)')} title={t('tip.openStrain')}>{t('table.view')}</button>
                            {canArchive && (s.is_archived ? (
                              <button type="button" onClick={() => restore(s)}
                                      style={smallBtn('var(--c-text-muted)')} title={t('tip.restore')}>{t('table.restore')}</button>
                            ) : (
                              <button type="button" onClick={() => { setArchiveError(''); setArchiveTarget(s); }}
                                      style={smallBtn('var(--c-warning-text-2)')} title={t('tip.archive')}>{t('table.archive')}</button>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {total > PAGE_SIZE && (
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 14 }}>
                  <button type="button" disabled={offset === 0}
                          onClick={() => setOffset(o => Math.max(0, o - PAGE_SIZE))}
                          style={btnStyle(offset === 0 ? 'var(--c-border-strong)' : 'var(--c-text-muted)')} title={t('tip.prev')}>
                    {t('pagination.prev')}
                  </button>
                  <button type="button" disabled={pageEnd >= total}
                          onClick={() => setOffset(o => o + PAGE_SIZE)}
                          style={btnStyle(pageEnd >= total ? 'var(--c-border-strong)' : 'var(--c-text-muted)')} title={t('tip.next')}>
                    {t('pagination.next')}
                  </button>
                </div>
              )}
            </>
          ))}
        </>
      )}

      {/* ── create / edit ── */}
      {showForm && (
        <div style={overlayStyle} onClick={() => !saving && setShowForm(false)}>
          <div role="dialog" aria-modal="true" style={{ ...modalStyle, width: 760 }} onClick={e => e.stopPropagation()}>
            <form onSubmit={submit} ref={formRef} data-testid="strain-form">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                <h3 ref={formHeadingRef} tabIndex={-1} style={{ margin: 0 }} data-testid="strain-form-title">
                  {editingId ? t('form.editTitle') : t('form.createTitle')}
                </h3>
                <button type="button" onClick={() => setShowForm(false)}
                        style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--c-text-muted-fg)', lineHeight: 1 }} title={t('tip.closeForm')}>×</button>
              </div>
              <p style={{ ...hintStyle, margin: '-8px 0 12px' }}>{t('common:validation.requiredNote')}</p>

              <div style={gridStyle}>
                <div style={labelStyle} data-field="isolate_code">
                  <label htmlFor="strain-isolate_code">{t('form.isolateCode')}</label>
                  <input id="strain-isolate_code" type="text" value={form.isolate_code} dir="ltr"
                         disabled={!!editingId}
                         onChange={e => { setForm(f => ({ ...f, isolate_code: e.target.value })); clearFieldError('isolate_code'); }}
                         placeholder={t('form.isolateCodePlaceholder')}
                         {...invalidProps(errorIdFor('strain-isolate_code'), !!fieldError('isolate_code'), 'strain-isolate_code-hint')}
                         style={{ ...inputStyle, background: editingId ? 'var(--c-bg-muted)' : 'var(--c-surface)', ...invalidBorder(!!fieldError('isolate_code')) }} title={t('tip.isolateCode')} />
                  <FieldError id={errorIdFor('strain-isolate_code')}>{validationMessage(t, fieldError('isolate_code'))}</FieldError>
                  <span id="strain-isolate_code-hint" style={hintStyle}>
                    {editingId ? t('form.isolateCodeLocked') : t('form.isolateCodeHint')}
                  </span>
                </div>
                <div style={labelStyle} data-field="species">
                  <label htmlFor="strain-species">{speciesRequired ? t('form.species') : t('form.speciesOptional')}</label>
                  <input id="strain-species" type="text" value={form.species} dir="ltr" aria-required={speciesRequired ? 'true' : undefined}
                         data-testid="strain-species"
                         onChange={e => { setForm(f => ({ ...f, species: e.target.value })); clearFieldError('species'); }}
                         placeholder={t('form.speciesPlaceholder')}
                         {...invalidProps(errorIdFor('strain-species'), !!fieldError('species'), 'strain-species-hint')}
                         style={{ ...inputStyle, ...invalidBorder(!!fieldError('species')) }} title={t('tip.species')} />
                  <FieldError id={errorIdFor('strain-species')} testId="strain-species-error">
                    {validationMessage(t, fieldError('species'))}
                  </FieldError>
                  <span id="strain-species-hint" style={hintStyle}>
                    {speciesRequired ? t('form.speciesHint') : t('form.speciesUnidentifiedHint')}
                  </span>
                </div>
                <label style={labelStyle}>
                  {t('form.source')}
                  <input type="text" value={form.source}
                         onChange={e => setForm(f => ({ ...f, source: e.target.value }))}
                         placeholder={t('form.sourcePlaceholder')} style={inputStyle} title={t('tip.source')} />
                </label>
                <label style={labelStyle}>
                  {t('form.acquisitionDate')}
                  <input type="date" value={form.acquisition_date}
                         onChange={e => setForm(f => ({ ...f, acquisition_date: e.target.value }))}
                         style={inputStyle} title={t('tip.acquisitionDate')} />
                </label>
              </div>

              <label style={{ ...labelStyle, marginBottom: 18 }}>
                {t('common:notes')}
                <textarea value={form.notes} rows={2}
                          onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                          style={{ ...inputStyle, resize: 'vertical' }} title={t('tip.notes')} />
              </label>

              {/* growth requirements, one card per stage */}
              <h4 style={{ margin: '0 0 4px' }}>{t('form.requirementsTitle')}</h4>
              <p style={{ ...hintStyle, margin: '0 0 12px' }}>{t('form.requirementsHint')}</p>

              {GROWTH_STAGES.map(stage => {
                const r = form.requirements[stage];
                // A fieldset: every stage has the same nine labels, so the
                // stage's name (its legend) is what tells a screen-reader
                // user which "pH min" an error is on.
                return (
                  <fieldset key={stage} data-testid={`strain-stage-${stage}`}
                            style={{ ...formStyle, background: 'var(--c-surface)', marginBottom: 12, marginInline: 0, minWidth: 0 }}>
                    <legend style={{ fontSize: 14, fontWeight: 700, padding: '0 4px' }}>{t(`stage.${stage}`)}</legend>
                    <div style={{ ...gridStyle, marginTop: 10, marginBottom: 0, gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))' }}>
                      {[
                        ['temp_min', 'req.tempMin'], ['temp_opt', 'req.tempOpt'], ['temp_max', 'req.tempMax'],
                        ['ph_min', 'req.phMin'], ['ph_opt', 'req.phOpt'], ['ph_max', 'req.phMax'],
                        ['humidity_min', 'req.humidityMin'], ['humidity_max', 'req.humidityMax'],
                        ['incubation_days', 'req.incubationDays'],
                      ].map(([field, key]) => {
                        const name = reqField(stage, field);
                        const id = inputId(name);
                        const err = fieldError(name);
                        return (
                          <div key={field} style={labelStyle} data-field={name}>
                            <label htmlFor={id}>{t(key)}</label>
                            <input id={id} type="number" step="any" value={r[field]}
                                   onChange={e => setReqField(stage, field, e.target.value)}
                                   {...invalidProps(errorIdFor(id), !!err)}
                                   style={{ ...inputStyle, ...invalidBorder(!!err) }} dir="ltr" title={field === 'incubation_days' ? t('tip.reqIncubation') : t('tip.reqBand')} />
                            <FieldError id={errorIdFor(id)} testId={`${id}-error`}>{validationMessage(t, err)}</FieldError>
                          </div>
                        );
                      })}
                      <label style={labelStyle}>
                        {t('req.substrate')}
                        <input type="text" value={r.substrate_type}
                               onChange={e => setReqField(stage, 'substrate_type', e.target.value)}
                               placeholder={t('req.substratePlaceholder')} style={inputStyle} title={t('tip.substrate')} />
                      </label>
                      <label style={labelStyle}>
                        {t('req.medium')}
                        <select value={r.protocol_id}
                                onChange={e => setReqField(stage, 'protocol_id', e.target.value)}
                                style={inputStyle} title={t('tip.medium')}>
                          <option value="">{t('common:selectPlaceholder')}</option>
                          {protocols.map(p => (
                            <option key={p.protocol_id} value={p.protocol_id}>{p.name}</option>
                          ))}
                        </select>
                      </label>
                      <label style={labelStyle}>
                        {t('req.lightRegime')}
                        <input type="text" value={r.light_regime}
                               onChange={e => setReqField(stage, 'light_regime', e.target.value)}
                               style={inputStyle} title={t('tip.lightRegime')} />
                      </label>
                    </div>
                  </fieldset>
                );
              })}

              {/* lineage */}
              <h4 style={{ margin: '18px 0 4px' }}>{t('form.lineageTitle')}</h4>
              <p style={{ ...hintStyle, margin: '0 0 12px' }}>{t('form.lineageHint')}</p>

              {form.lineage.map((link, i) => (
                <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-end', marginBottom: 10, flexWrap: 'wrap' }}>
                  <label style={{ ...labelStyle, flex: '2 1 220px' }}>
                    {t('form.parent')}
                    <select value={link.parent_strain_id}
                            onChange={e => setLineageField(i, 'parent_strain_id', e.target.value)}
                            style={inputStyle} title={t('tip.parent')}>
                      <option value="">{t('common:selectPlaceholder')}</option>
                      {items.filter(s => s.id !== editingId).map(s => (
                        <option key={s.id} value={s.id}>
                          {s.isolate_code}{s.species ? ` · ${s.species}` : ''}
                        </option>
                      ))}
                    </select>
                  </label>
                  {(() => {
                    const name = passageField(i);
                    const id = inputId(name);
                    const err = fieldError(name);
                    return (
                      <div style={{ ...labelStyle, flex: '0 1 120px' }} data-field={name}>
                        <label htmlFor={id}>{t('form.passageNumber')}</label>
                        <input id={id} type="number" value={link.passage_number} dir="ltr"
                               onChange={e => setLineageField(i, 'passage_number', e.target.value)}
                               {...invalidProps(errorIdFor(id), !!err)}
                               style={{ ...inputStyle, ...invalidBorder(!!err) }} title={t('tip.passage')} />
                        <FieldError id={errorIdFor(id)}>{validationMessage(t, err)}</FieldError>
                      </div>
                    );
                  })()}
                  <label style={{ ...labelStyle, flex: '0 1 140px' }}>
                    {t('form.generationCode')}
                    <input type="text" value={link.generation_code} dir="ltr"
                           placeholder={t('form.generationCodePlaceholder')}
                           onChange={e => setLineageField(i, 'generation_code', e.target.value)}
                           style={inputStyle} title={t('tip.generation')} />
                  </label>
                  <button type="button" style={smallBtn('var(--c-danger)')}
                          onClick={() => {
                            setForm(f => ({ ...f, lineage: f.lineage.filter((_, j) => j !== i) }));
                            // Rows below move up a place, and their errors would not.
                            clearFieldError(name => name.startsWith('lineage.'));
                          }} title={t('tip.removeParent')}>
                    {t('form.removeParent')}
                  </button>
                </div>
              ))}
              <button type="button" style={smallBtn('var(--c-text-muted)')}
                      onClick={() => setForm(f => ({
                        ...f,
                        lineage: [...f.lineage, { parent_strain_id: '', passage_number: '', generation_code: '' }],
                      }))} title={t('tip.addParent')}>
                {t('form.addParent')}
              </button>

              {formError && <p role="alert" data-testid="strain-form-error" style={{ color: 'var(--c-danger-fg)', fontSize: 13, marginTop: 14 }}>{formError}</p>}

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
                <button type="button" onClick={() => setShowForm(false)} disabled={saving}
                        style={btnStyle('var(--c-text-muted)')} title={t('tip.closeForm')}>{t('common:cancel')}</button>
                <button type="submit" disabled={saving} style={btnStyle('var(--c-primary)')} title={t('tip.save')}>
                  {saving ? t('common:saving') : t('common:save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── detail ── */}
      {(detail || detailLoading) && (
        <div style={overlayStyle} onClick={() => { setDetail(null); }}>
          <div role="dialog" aria-modal="true" style={{ ...modalStyle, width: 720, maxHeight: '86vh', overflowY: 'auto' }}
               onClick={e => e.stopPropagation()}>
            {detailLoading && (
              <OrganicLoader block motif="lineage" label={t('common:loading')} slowLabel={t('common:stillWorking')} />
            )}
            {detail && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
                  <div>
                    <h3 style={{ margin: '0 0 2px' }} dir="ltr">{detail.isolate_code}</h3>
                    <div data-testid="strain-qr" style={{ margin: '6px 0' }}><QRCodeLabel value={detail.isolate_code} size={56} /></div>
                    <p style={{ margin: 0, fontSize: 13, color: 'var(--c-text-muted-fg)' }}>
                      <em dir="ltr">{detail.species || t('detail.noSpecies')}</em>
                      {detail.is_archived && (
                        <span style={{ marginInlineStart: 8, color: 'var(--c-warning-text-2-fg)', fontWeight: 600 }}>
                          {t('table.archived')}
                        </span>
                      )}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    {canEdit && !detail.is_archived && (
                      <button type="button" onClick={() => { openEdit(detail); setDetail(null); }}
                              style={smallBtn('var(--c-primary)')} title={t('tip.edit')}>{t('common:edit')}</button>
                    )}
                    <button type="button" onClick={() => setDetail(null)}
                            style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--c-text-muted-fg)', lineHeight: 1 }} title={t('tip.closeDetail')}>×</button>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 12px', fontSize: 13, marginBottom: 20 }}>
                  <div style={{ color: 'var(--c-text-muted-fg)' }}>{t('form.source')}</div>
                  <div>{detail.source || '—'}</div>
                  <div style={{ color: 'var(--c-text-muted-fg)' }}>{t('form.acquisitionDate')}</div>
                  <div>{detail.acquisition_date ? new Date(detail.acquisition_date).toLocaleDateString() : '—'}</div>
                  <div style={{ color: 'var(--c-text-muted-fg)' }}>{t('detail.created')}</div>
                  <div>{detail.created_at ? new Date(detail.created_at).toLocaleDateString() : '—'}</div>
                  {detail.notes && (<>
                    <div style={{ color: 'var(--c-text-muted-fg)' }}>{t('common:notes')}</div>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{detail.notes}</div>
                  </>)}
                </div>

                {/* The strain's species' scientific profile (its family's where the
                    species has none): species-level data, never the strain's own. */}
                {detail.species && (
                  <SpeciesBioProfile name={detail.species} tier="full" user={user} testId="strain-bioprofile" />
                )}

                {/* growth requirements */}
                <h4 style={sectionTitle}>{t('detail.requirements')}</h4>
                {(detail.growth_requirements || []).length === 0 ? (
                  <p style={{ ...hintStyle, marginTop: 0 }}>{t('detail.noRequirements')}</p>
                ) : (
                  <div className="table-wrap">
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 560 }}>
                      <thead>
                        <tr style={{ background: 'var(--c-bg-muted-2)' }}>
                          {['stageCol', 'temperature', 'ph', 'humidity', 'substrate', 'medium', 'incubation']
                            .map(h => <th key={h} style={thStyle}>{t(`detail.${h}`)}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {detail.growth_requirements.map(r => (
                          <tr key={r.id} style={{ borderBottom: '1px solid var(--c-bg-muted-3)' }}>
                            <td style={tdStyle}>{t(`stage.${r.growth_stage}`)}</td>
                            <td style={tdStyle} dir="ltr">
                              {formatBand(r.temp_min, r.temp_opt, r.temp_max, ' °C', '—')}
                            </td>
                            <td style={tdStyle} dir="ltr">
                              {formatBand(r.ph_min, r.ph_opt, r.ph_max, '', '—')}
                            </td>
                            <td style={tdStyle} dir="ltr">
                              {formatBand(r.humidity_min, null, r.humidity_max, ' %', '—')}
                            </td>
                            <td style={tdStyle}>{r.substrate_type || '—'}</td>
                            <td style={tdStyle} dir="ltr">{r.protocol_name || '—'}</td>
                            <td style={tdStyle} dir="ltr">
                              {r.incubation_days ? t('detail.days', { n: r.incubation_days }) : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <p style={{ ...hintStyle, marginTop: 6 }}>{t('detail.bandLegend')}</p>

                {/* lineage */}
                <h4 style={sectionTitle}>{t('detail.lineage')}</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
                  {[['parents', detail.lineage?.parents || []],
                    ['children', detail.lineage?.children || []]].map(([key, list]) => (
                    <div key={key}>
                      <strong style={{ fontSize: 13 }}>{t(`detail.${key}`)}</strong>
                      {list.length === 0 ? (
                        <p style={{ ...hintStyle, marginTop: 4 }}>
                          {t(key === 'parents' ? 'detail.noParents' : 'detail.noChildren')}
                        </p>
                      ) : (
                        <ul style={{ margin: '6px 0 0', paddingInlineStart: 18, fontSize: 13 }}>
                          {list.map(l => (
                            <li key={l.lineage_id} style={{ marginBottom: 4 }}>
                              <button type="button" onClick={() => openDetail(l.strain_id)}
                                      style={linkBtn} dir="ltr" title={t('tip.openLinked')}>{l.isolate_code}</button>
                              {l.passage_number !== null && l.passage_number !== undefined && (
                                <span style={{ color: 'var(--c-text-muted-fg)' }}>
                                  {' · '}{t('detail.passage', { n: l.passage_number })}
                                </span>
                              )}
                              {l.generation_code && (
                                <span style={{ color: 'var(--c-text-muted-fg)' }} dir="ltr">{' · '}{l.generation_code}</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>

                {/* holdings */}
                <h4 style={sectionTitle}>{t('detail.holdings')}</h4>
                <p style={{ ...hintStyle, marginTop: 0 }}>
                  {detail.holdings?.species
                    ? t('detail.holdingsScopeNote', { species: detail.holdings.species })
                    : t('detail.holdingsNoSpecies')}
                </p>
                {detail.holdings?.species && (
                  <>
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
                      {[['collections', detail.holdings.collection_count],
                        ['plates', detail.holdings.plate_count],
                        ['activeSlants', detail.holdings.active_slant_count],
                        ['dnaRecords', detail.holdings.dna_count]].map(([key, value]) => (
                        <div key={key} style={statTile}>
                          <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--c-primary-fg)' }}>{value ?? 0}</div>
                          <div style={{ fontSize: 12, color: 'var(--c-text-muted-fg)' }}>{t(`detail.${key}`)}</div>
                        </div>
                      ))}
                    </div>
                    {Object.keys(detail.holdings.plate_status_counts || {}).length > 0 && (
                      <div style={{ marginBottom: 8 }}>
                        <span style={{ fontSize: 12, color: 'var(--c-text-muted-fg)', marginInlineEnd: 8 }}>
                          {t('detail.plates')}:
                        </span>
                        {Object.entries(detail.holdings.plate_status_counts).map(([status, n]) =>
                          badge(status, PLATE_STATUS_COLORS, `${status.replace(/_/g, ' ')} · ${n}`))}
                      </div>
                    )}
                    {Object.keys(detail.holdings.slant_status_counts || {}).length > 0 && (
                      <div style={{ marginBottom: 8 }}>
                        <span style={{ fontSize: 12, color: 'var(--c-text-muted-fg)', marginInlineEnd: 8 }}>
                          {t('detail.slants')}:
                        </span>
                        {Object.entries(detail.holdings.slant_status_counts).map(([status, n]) =>
                          badge(status, SLANT_STATUS_COLORS, `${status.replace(/_/g, ' ')} · ${n}`))}
                      </div>
                    )}
                    {(detail.holdings.regions || []).length > 0 && (
                      <p style={{ fontSize: 13, margin: '8px 0 0' }}>
                        <span style={{ color: 'var(--c-text-muted-fg)' }}>{t('detail.regions')}: </span>
                        {detail.holdings.regions.join(', ')}
                      </p>
                    )}
                    {(detail.holdings.photos || []).length > 0 && (
                      <div style={{ marginTop: 12 }}>
                        <PhotoGallery photos={detail.holdings.photos} thumbWidth={84} thumbHeight={84} />
                      </div>
                    )}
                  </>
                )}

                {canArchive && (
                  <div style={{ marginTop: 20, borderTop: '1px solid var(--c-border)', paddingTop: 14 }}>
                    {detail.is_archived ? (
                      <button type="button" onClick={() => restore(detail)} style={btnStyle('var(--c-text-muted)')} title={t('tip.restore')}>
                        {t('table.restore')}
                      </button>
                    ) : (
                      <button type="button" onClick={() => { setArchiveError(''); setArchiveTarget(detail); }}
                              style={btnStyle('var(--c-warning-text-2)')} title={t('tip.archive')}>
                        {t('table.archive')}
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      <ConfirmDeleteModal
        open={!!archiveTarget}
        title={t('archiveConfirm.title', { code: archiveTarget?.isolate_code || '' })}
        consequences={[
          t('archiveConfirm.hidden'),
          t('archiveConfirm.kept'),
          t('archiveConfirm.reversible'),
        ]}
        deleting={archiving}
        error={archiveError}
        confirmLabel={t('table.archive')}
        confirmingLabel={t('archiveConfirm.archiving')}
        confirmColor="var(--c-warning-text-2)"
        onConfirm={confirmArchive}
        onCancel={() => { setArchiveTarget(null); setArchiveError(''); }}
      />
    </div>
  );
}

const thStyle = { padding: '8px 12px', textAlign: 'start', fontWeight: 600 };
const tdStyle = { padding: '8px 12px' };

const formStyle = {
  background: 'var(--c-bg-subtle)',
  border: '1px solid var(--c-border)',
  borderRadius: 8,
  padding: 20,
  marginBottom: 24,
};

const gridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
  gap: 14,
  marginBottom: 16,
};

const labelStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  fontSize: 14,
  fontWeight: 500,
};

const hintStyle = { fontSize: 12, color: 'var(--c-text-muted-fg)', fontWeight: 400 };

const sectionTitle = { margin: '20px 0 8px', paddingTop: 12, borderTop: '1px solid var(--c-border)' };

const statTile = {
  background: 'var(--c-bg-subtle)',
  border: '1px solid var(--c-border)',
  borderRadius: 8,
  padding: '10px 18px',
  textAlign: 'center',
  minWidth: 88,
};

const inputStyle = {
  padding: '6px 10px',
  border: '1px solid var(--c-border-strong)',
  borderRadius: 6,
  fontSize: 14,
  fontWeight: 400,
};

const btnStyle = (bg) => ({
  background: bg,
  color: 'var(--c-on-accent)',
  border: 'none',
  borderRadius: 6,
  padding: '8px 16px',
  cursor: 'pointer',
  fontSize: 14,
});

const smallBtn = (bg) => ({
  background: bg,
  color: 'var(--c-on-accent)',
  border: 'none',
  borderRadius: 4,
  padding: '3px 10px',
  cursor: 'pointer',
  fontSize: 12,
});

const linkBtn = {
  background: 'none',
  border: 'none',
  padding: 0,
  color: 'var(--c-primary-fg)',
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: 13,
  fontFamily: 'monospace',
};

// A dialog taller than the window used to be centred by the overlay, which
// pushed its top (title, close button, first field) above the screen where
// nothing could scroll to it. The overlay scrolls now, and margin:auto on
// the dialog centres it only while it fits.
const overlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.4)',
  display: 'flex',
  alignItems: 'flex-start',
  overflowY: 'auto',
  padding: '24px 16px',
  justifyContent: 'center',
  zIndex: 1000,
};

const modalStyle = {
  background: 'var(--c-surface)',
  margin: 'auto',
  borderRadius: 10,
  padding: 28,
  width: 420,
  maxWidth: '90vw',
  boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
};
